import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { describeError } from '@/api/errors';
import { useOrgId } from '@/app/session';
import { Button, Select, useToast } from '@/components/ui';
import { addressesOf, type Conversation, type Message } from './api';
import { AiMissing, useAi } from './ai';
import { createReviewedDraft, draftInvalidations, generateAiDraft, useSenderIdentities, type DraftRecipientInput } from './drafts';
import { RecipientLines, useOurAddresses, type Recipient } from './parts';

/** Who a reply goes to: the author of the latest inbound mail plus its other external recipients. */
function replyRecipients(c: Conversation, messages: Message[], ours: ReadonlySet<string>): DraftRecipientInput[] {
  const lastIn = [...messages].reverse().find((m) => m.kind === 'inbound_email');
  const out: DraftRecipientInput[] = [];
  const seen = new Set<string>();
  const add = (kind: 'to' | 'cc', email: string, name: string | null | undefined) => {
    const key = email.toLowerCase();
    if (ours.has(key) || seen.has(key)) return;
    seen.add(key);
    out.push({ kind, email, display_name: name ?? null });
  };
  if (lastIn) {
    for (const a of [...addressesOf(lastIn, 'reply_to'), ...addressesOf(lastIn, 'from')].slice(0, 1)) add('to', a.email_original, a.display_name);
    for (const a of addressesOf(lastIn, 'to')) add('cc', a.email_original, a.display_name);
    for (const a of addressesOf(lastIn, 'cc')) add('cc', a.email_original, a.display_name);
  }
  for (const p of c.participants) {
    if (p.status === 'inactive' || p.role === 'mentioned' || p.role === 'bcc') continue;
    add(out.some((r) => r.kind === 'to') ? 'cc' : 'to', p.email_original, p.display_name);
  }
  return out;
}

function replySubject(c: Conversation, messages: Message[]): string {
  const s = messages.at(-1)?.subject || c.title;
  return /^re:/i.test(s) ? s : `Re: ${s}`;
}

/**
 * Bottom of a thread without a draft: the reply on paper, ready to type into. Saving makes it a
 * draft to approve; Draft with AI writes one, taking whatever is typed as notes.
 */
export function Composer({ conversation: c, messages, contextReady = true }: { conversation: Conversation; messages: Message[]; contextReady?: boolean }) {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const sourceReply = c.origin === 'source_recorded';
  const [recipientEmail, setRecipientEmail] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const ai = useAi();
  const senders = useSenderIdentities();
  const [body, setBody] = useState('');
  const [senderChoice, setSenderChoice] = useState<string | null>(null);
  const senderId = senderChoice ?? '';

  const ours = useOurAddresses(messages);
  const inferred = useMemo(() => replyRecipients(c, messages, ours), [c, messages, ours]);
  const recipients: DraftRecipientInput[] = sourceReply || inferred.length === 0 ? (recipientEmail.trim() ? [{ kind: 'to', email: recipientEmail.trim(), display_name: null }] : []) : inferred;
  const lastIn = [...messages].reverse().find((m) => m.kind === 'inbound_email');
  const cp = c.counterpart;
  const chips: Recipient[] = recipients.map((r) => {
    const counterpart = cp && cp.email.toLowerCase() === r.email.toLowerCase();
    return {
      kind: r.kind === 'cc' ? 'cc' : 'to',
      email: r.email,
      name: r.display_name ?? (counterpart ? cp.name : null) ?? r.email,
      company: counterpart ? cp.company_name : null,
    };
  });

  const done = (title: string) => {
    toast.show({ title });
    setBody('');
    return Promise.all(draftInvalidations(orgId, c.id).map((k) => qc.invalidateQueries({ queryKey: k })));
  };

  const reply = useMutation({
    mutationFn: () => {
      if (!contextReady) throw new Error('Conversation or draft list unavailable. Retry before saving.');
      return createReviewedDraft(orgId, c.id, {
        sender_identity_id: senderId,
        subject: replySubject(c, messages),
        text_body: body,
        in_reply_to_message_id: sourceReply ? null : lastIn?.id ?? null,
        recipients,
        ...(sourceReply ? { source_reply_intent: true } : {}),
      }, sourceReply ? c.version : undefined);
    },
    onSuccess: async (draft) => {
      await done(draft.reviewUnavailable ? 'Draft saved — review unavailable; open saved draft to retry review' : 'Draft saved — review before sending');
      await Promise.all(draftInvalidations(orgId, draft.conversation_id).map((k) => qc.invalidateQueries({ queryKey: k })));
      if (sourceReply) await navigate({ to: '/app/conversations/$conversationId', params: { conversationId: draft.conversation_id }, search: {} });
    },
    onError: (e) => toast.show({ tone: 'accent', title: 'Draft not saved', body: describeError(e).body, durationMs: 10_000 }),
  });

  const draftAi = useMutation({
    mutationFn: () => {
      if (!contextReady) throw new Error('Conversation or draft list unavailable. Retry before drafting.');
      if (ai.state !== 'ready') throw new Error('No AI provider');
      return generateAiDraft(orgId, c, ai.providerId, body);
    },
    onSuccess: () => done('AI draft ready'),
    onError: (e) => toast.show({ tone: 'accent', title: 'AI draft failed', body: describeError(e).body, durationMs: 10_000 }),
  });

  const busy = reply.isPending || draftAi.isPending;
  const noSender = !senders.pending && senders.identities.length === 0;
  const first = cp?.name?.split(' ')[0];

  return (
    <form
      className="reply composer"
      aria-label="Reply"
      onSubmit={(e) => {
        e.preventDefault();
        if (contextReady && body.trim() && senderId && recipients.length) reply.mutate();
      }}
    >
      <div className="paper">
        {sourceReply || inferred.length === 0 ? <label className="chat-recipient">To <input type="email" required value={recipientEmail} placeholder="Select recipient: enter confirmed email" onChange={event => setRecipientEmail(event.target.value)} /></label> : <RecipientLines recipients={chips} />}
        {(senders.identities.length > 0) ? (
          <Select
            aria-label="From"
            value={senderId}
            options={[{ value: '', label: 'Select sender mailbox' }, ...senders.identities.map((s) => ({ value: s.id, label: `From ${s.display_name} <${s.email}>` }))]}
            onChange={(e) => setSenderChoice(e.target.value)}
          />
        ) : null}
        {sourceReply ? <p className="chat-source-reply">New email draft · history stays unchanged and automation stays paused.</p> : null}
        <p className="paper-subject">{replySubject(c, messages)}</p>
        <label className="visually-hidden" htmlFor={`composer-body-${c.id}`}>
          Reply
        </label>
        <textarea
          id={`composer-body-${c.id}`}
          className="paper-body"
          value={body}
          maxLength={100000}
          readOnly={busy}
          placeholder={first ? `Reply to ${first}, or notes for AI` : 'Reply, or notes for AI'}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <footer className="reply-bar composer-bar">
        <Button type="submit" variant="primary" size="sm" loading={reply.isPending} disabled={busy || !contextReady || !body.trim() || !senderId || recipients.length === 0}>
          Save draft
        </Button>
        <Button
          size="sm"
          variant="quiet"
          icon="sparkle"
          loading={draftAi.isPending}
          disabled={sourceReply || busy || !contextReady || ai.state !== 'ready' || !c.primary_location_id}
          title={!c.primary_location_id ? 'No site linked' : undefined}
          onClick={() => draftAi.mutate()}
        >
          {draftAi.isPending ? 'Drafting' : 'Draft with AI'}
        </Button>
        <span className="reply-bar-side composer-notes">
          {ai.state === 'missing' ? <AiMissing /> : null}
          {noSender ? (
            <span className="ai-missing">
              No mailbox. <Link to="/app/settings/mailbox">Add mailbox</Link>
            </span>
          ) : null}
        </span>
      </footer>
    </form>
  );
}
